import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Message, ConfirmationService, SelectItem } from 'primeng/api';
import { saveAs } from 'file-saver';

import { EvaluationSystemService } from '../../services/evaluation-system.service';
import { EvaluationModel } from '../../models/evaluation-model';
import { ValidationService } from '../../services/validation.service';
import { RdpService } from '../../services/rdp.service';
import { environment } from '../../../environments/environment';
import { FileService } from '../../services/file.service';
import { IsAdmin } from '../../guards/isAdmin';
import { Computer, EvaluationService, StartEvaluationParameters } from '../../services/GI.Web.Services.g';
import { ComputerStates } from '../../models/computerState';
import { EvaluationStates } from '../../models/evaluationState';
import { ErrorHandling } from '../../ErrorHandling';

@Component({
  selector: 'app-admin-connect-detail',
  templateUrl: './admin-connect-detail.component.html',
  styleUrls: ['./admin-connect-detail.component.css']
})
export class AdminConnectDetailComponent implements OnInit, OnDestroy {
  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _evaluationService: EvaluationService,
    private _evaluationSystemService: EvaluationSystemService,
    private _confirmationService: ConfirmationService,
    private _fileService: FileService,
    private _validationService: ValidationService,
    private _rdpService: RdpService,
    public _adminGuard: IsAdmin) { }

  private _id: string;
  public evaluation: EvaluationModel = null;
  public userToAdd: string;
  public noteToAdd: string;
  public isNew = false;
  public errorMessages: Message[] = [];
  public stateMessages: Message[] = [];
  public showStartEvalDialog = false;
  public startingEval = false;
  public passwordVerification: string = null;
  private viewInitialized = false;
  private saveStartDialogButtonEvent: Observable<any>;
  public computers: Computer[] = [];
  public cols: any[] = [];
  public computerStates: any = ComputerStates;
  public creating = false;
  public updating = false;
  public paradiseVersions: SelectItem[] = [];
  public revealList: string[] = [];
  public loading = true;
  private stopRefreshing = false;
  public emailInvalid = false;
  public userNameConditions: string[] = ['\\', '/', '"', '[', ']', ':', '|', '<', '>', '+', '=', ';', ',', '?', '*', '@'];
  public userNameInvalid = false;
  public passwordInvalid = false;

  // Binding UI to parallel properties because this.evaluation gets set when polling for updates in this.refreshEvaluation()
  public updateDisplayName: string;
  public updateStart: Date;
  public updateEnd: Date;
  public updateHours: number;
  public updateNotes: string;


  ngOnInit() {
    this._adminGuard.canActivate();
    this._route.params.subscribe(params => {
      this._id = params['id'];
      if (!this._id || this._id === 'new') {
        this.getParadiseVersions();
        this.createNewEvaluation();
      }
      else {
        this.getEvaluationDetails(this._id);
      }
    });
  }

  async getEvaluationDetails(id: string) {
    try {
      this.evaluation = await this._evaluationSystemService.getEvaluation(id).toPromise();
      this.initializeComputers();
      this.initializeUpdateProperties();
      this.setColumns();
      this.checkIfElementsReady();
      if (this.evaluation.state === EvaluationStates.deploying) {
        this.updateMessageBannerForDeployment(EvaluationStates.deploying);
      }
      // Always start refresh
      this.refreshEvaluation();
      this.loading = false;
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error);
    }
  }

  refreshEvaluation() {
    if (!this.stopRefreshing) {
      this._evaluationSystemService.getEvaluation(this.evaluation.name).subscribe(result => {
        if (result.state !== this.evaluation.state) {
          this.updateMessageBannerForDeployment(result.state);
        }
        this.startingEval = false;  // unclear disable of button while waiting for 1rst refresh
        this.evaluation = result;
        this.updateComputers();
        setTimeout(() => this.refreshEvaluation(), environment.pollTime);
        // alert("refreshing");
      });
    }
  }

  initializeComputers() {
    this.computers = [];
    if (this.evaluation.server) {
      this.computers.push(this.evaluation.server);
    }
    this.evaluation.clients.forEach(client => {
      this.computers.push(client);
    });
  }

  updateComputers() {
    if (this.evaluation.server) {
      // When creating, no computers are set so, initialize
      if (this.computers == null || !(this.computers.length)) {
        this.initializeComputers();
      }

      const server = this.computers.find(c => c.isServer);
      this.updateComputerState(this.evaluation.server.state, server);

      this.evaluation.clients.forEach(client => {
        const targetComputer = this.computers.find(computer => computer.resourceId === client.resourceId);
        this.updateComputerState(client.state, targetComputer);
      });
    }
  }

  updateComputerState(newState: string, targetComputer: Computer) {
    for (const computer of this.computers) {
      if (computer.resourceId === targetComputer.resourceId) {
        // Since view is ahead of server response only update under the following conditions
        if (newState === ComputerStates.starting && (computer.state === ComputerStates.stopped || computer.state === ComputerStates.unknown)) {
          computer.state = ComputerStates.starting;
          this.updateMessageBannerForClientState(ComputerStates.starting, computer.computerName);
        }
        else if (newState === ComputerStates.running && (computer.state === ComputerStates.starting || computer.state === ComputerStates.unknown)) {
          computer.state = ComputerStates.running;
          // Also update IP Address on running instances
          this.updateIPAddress(computer);
          this.updateMessageBannerForClientState(ComputerStates.running, computer.computerName);
        }
        else if (newState === ComputerStates.stopping && (computer.state === ComputerStates.running || computer.state === ComputerStates.unknown)) {
          computer.state = ComputerStates.stopping;
          this.updateMessageBannerForClientState(ComputerStates.stopping, computer.computerName);
        }
        else if (newState === ComputerStates.stopped && (computer.state === ComputerStates.stopping || computer.state === ComputerStates.unknown)) {
          computer.state = ComputerStates.stopped;
          this.updateMessageBannerForClientState(ComputerStates.stopped, computer.computerName);
        }
      }
    }
  }

  updateIPAddress(targetComputer: Computer) {
    if (targetComputer.isServer) {
      targetComputer.ipAddress = this.evaluation.server.ipAddress;
    }
    else {
      this.evaluation.clients.forEach(client => {
        if (client.resourceId === targetComputer.resourceId) {
          targetComputer.ipAddress = client.ipAddress;
        }
      });
    }
  }

  getParadiseVersions() {
    const paradiseVersions = [];
    this._fileService.getFilesFull(true, true, true, null).subscribe(results => {
      if (results) {
        const versions = results[1];
        versions.forEach(version => {
          if (version.name.includes('ParadiseSetup')) {
            const beginningIndex = ('Paradise/').length;
            const endindingIndex = version.name.indexOf('/', beginningIndex);
            if (version.beta) {
              this.paradiseVersions.push({
                label: version.name.substring(beginningIndex, endindingIndex) + ' (beta)',
                value: version.name.substring(beginningIndex, endindingIndex)
              });
            }
            else if (version.archive) {
              this.paradiseVersions.push({
                label: version.name.substring(beginningIndex, endindingIndex) + ' (archived)',
                value: version.name.substring(beginningIndex, endindingIndex)
              });
            }
            else {
              this.paradiseVersions.push({
                label: version.name.substring(beginningIndex, endindingIndex),
                value: version.name.substring(beginningIndex, endindingIndex)
              });
            }

            // set default version based on current release
            if (version.release && !version.archive && !version.beta) {
              this.evaluation.paradiseVersion = version.name.substring(beginningIndex, endindingIndex);
            }
          }

        });
        this.paradiseVersions.sort((a, b) => {
          const aValue = this.calculateVersionValue(a.label);
          const bValue = this.calculateVersionValue(b.label);
          if (aValue > bValue) {
            return 1;
          }
          if (aValue < bValue) {
            return -1;
          }
          return 0;
        });
        this.loading = false;
      }

    });
  }

  calculateVersionValue(version: string): number {
    const firstPeriodIndex = version.indexOf('.');
    const secondPeriodIndex = version.lastIndexOf('.');
    const hundredThousands = parseInt(version.substring(0, firstPeriodIndex), 10) * 100000;
    const thousands = parseInt(version.substring(firstPeriodIndex + 1, secondPeriodIndex), 10) * 10000;
    const ones = parseInt(version.substring(secondPeriodIndex + 1), 10);
    return hundredThousands + thousands + ones;
  }

  initializeUpdateProperties() {
    this.updateDisplayName = this.evaluation.displayName;
    this.updateStart = this.evaluation.start;
    this.updateEnd = this.evaluation.end;
    this.updateHours = this.evaluation.interactiveCreditsAvailable;
    this.updateNotes = this.evaluation.notes;
  }

  createNewEvaluation() {
    this.isNew = true;
    this.evaluation = new EvaluationModel();
    this.evaluation.name = null; // "Name" + UUID.UUID();
    this.evaluation.state = EvaluationStates.notStarted;
    this.evaluation.start = new Date(Date.now());
    this.evaluation.end = new Date(Date.now() + 30 * (1000 * 60 * 60 * 24));
    this.evaluation.interactiveCredits = 80;
    this.evaluation.interactiveCreditsUsed = 0;
    // this.evaluation.storage = 1024;
    // this.evaluation.storageUsed = 0;
    // this.evaluation.calculateAvailableStorage();
    this.evaluation.notes = '';
    this.evaluation.paradiseVersion = environment.defaultParadiseVersion;
    this.evaluation.serverVmSize = environment.defaultParadiseServerVmSize;

    this.initializeUpdateProperties();
  }

  initializeDialogEvents() {
    const saveStartDialogButton = document.getElementById('saveStartEvalParametersButton');
    this.saveStartDialogButtonEvent = Observable.fromEvent(saveStartDialogButton, 'click');
  }

  checkIfElementsReady() {
    if (this.viewInitialized) {
      this.initializeDialogEvents();
    }
  }

  setColumns() {
    this.cols = [
      { field: 'isServer', header: 'Role', width: '10%' },
      { field: 'state', header: 'State', width: '20%' },
      { field: 'user', header: 'User', width: '30%' },
      { field: '', header: 'Action', width: '40%' }
    ];
  }

  removeUser(userEmail: string) {

  }

  updateCredits() {
    // since the UI is modifying CreditsAvailable which is a calculated value, must work backwards to update issued value
    this.evaluation.interactiveCredits = this.updateHours + this.evaluation.interactiveCreditsUsed;
  }

  addUser() {
    this.evaluation.users.push(this.userToAdd);
    this.userToAdd = null;
  }

  addNote() {
    this.evaluation.notes = this.evaluation.notes + this.noteToAdd;
    this.noteToAdd = null;
  }

  async update() {
    this.stopRefreshing = true;
    this.updating = true;
    this.evaluation.displayName = this.updateDisplayName;
    this.evaluation.start = this.updateStart;
    this.evaluation.end = this.updateEnd;
    this.evaluation.notes = this.updateNotes;
    try {
      await this._evaluationService.put(this.evaluation).toPromise();
      this._router.navigate(['/adminconnectlist/']);
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error);
    }
    this.updating = false;
  }

  async create() {
    this.errorMessages = [];
    this.creating = true;

    // populate update fields from form to evaluation
    this.evaluation.displayName = this.updateDisplayName;
    this.evaluation.start = this.updateStart;
    this.evaluation.end = this.updateEnd;
    this.evaluation.interactiveCredits = this.updateHours;
    this.evaluation.notes = this.updateNotes;

    try {
      const createResult = await this._evaluationSystemService.createEvaluation(this.evaluation).toPromise();
      // Auto-start deployment
      await this.startEvaluationAfterCreation(createResult);
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error, 'Could not Create Evaluation:');
    }
    this.creating = false;
  }

  // TODO: Refactor 2 startEvaluation functions below
  private async startEvaluationAfterCreation(evaluation: EvaluationModel) {
    if (!evaluation) {
      return;
    }
    const parameters = new StartEvaluationParameters({ userName: evaluation.vmUserName, password: evaluation.vmPassword });
    try {
      await this._evaluationService.startEvaluation(evaluation.name, parameters).toPromise();
      // Tried redirecting to detail page but since already on detail page, ngOnIt doesn't run and
      // the component thinks it still a new evaluation
      // this._router.navigate(["/adminconnectdetail/" + createResult.name]);
      this._router.navigate(['/adminconnectlist']);
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error, 'Could not Start Evaluation:');
      // TODO: Note that the user is caught in a weird state here... The Evaluation has been created, but they are now stuck on the /new page.
      //       If the user clicks Create again it will probably fail because the Evaluation w/ the name already exists
    }
  }

  private async startEvaluation(evaluation: EvaluationModel) {
    const parameters = new StartEvaluationParameters({ userName: evaluation.vmUserName, password: evaluation.vmPassword });
    try {
      await this._evaluationService.startEvaluation(evaluation.name, parameters).toPromise();
      this.refreshEvaluation();
    } catch (error) {
      this.errorMessages = ErrorHandling.getErrorMessages(error, 'Could not Start Evaluation:');
    }
  }

  confirmStart() {
    this.showStartEvalDialog = true;
    this.startingEval = true;
    this._confirmationService.confirm({
      message: `Are you sure you want to START ${this.evaluation.displayName}?`,
      accept: () => this.startEvaluation(this.evaluation)
    });
  }

  confirmEnd() {
    this._confirmationService.confirm({
      message: `Are you sure you want to END ${this.evaluation.displayName}?`,
      accept: () => this.end()
    });
  }

  async end() {
    try {
      await this._evaluationService.stopEvaluation(this.evaluation.name).toPromise();
      this._router.navigate(['/adminconnectlist/']);
    } catch (error) {
      ErrorHandling.getErrorMessages(error);
    }
  }

  confirmDelete() {
    this._confirmationService.confirm({
      message: `Are you sure you want to DELETE ${this.evaluation.displayName}?`,
      accept: () => this.delete()
    });
  }

  async delete() {
    this.stopRefreshing = true;
    try {
      await this._evaluationService.delete(this.evaluation.name).toPromise();
      this._router.navigate(['/adminconnectlist/']);
    } catch (error) {
      ErrorHandling.getErrorMessages(error);
    }
  }

  startInstance(computer: Computer) {
    const currentState = computer.state;
    this.updateComputerState(ComputerStates.starting, computer);
    if (computer.isServer) {
      this.startServer(computer, currentState);
    }
    else {
      this.startClient(computer, currentState);
    }
  }

  private async startClient(computer: Computer, currentState: string) {
    try {
      await this._evaluationService.startClient(this.evaluation.name, computer.user).toPromise();
    } catch (error) {
      ErrorHandling.getErrorMessages(error, `Could not Start VM ${computer.resourceId}:`);
      computer.state = currentState;
    }
  }

  private async startServer(computer: Computer, currentState: string) {
    try {
      await this._evaluationService.startServer(this.evaluation.name).toPromise();
    } catch (error) {
      ErrorHandling.getErrorMessages(error, `Could not Start VM ${computer.resourceId}:`);
      computer.state = currentState;
    }
  }

  stopInstance(computer: Computer) {
    const currentState = computer.state;
    this.updateComputerState(ComputerStates.stopping, computer);
    if (computer.isServer) {
      this.stopServer(computer, currentState);
    }
    else {
      this.stopClient(computer, currentState);
    }
  }

  private async stopClient(computer: Computer, currentState: string) {
    try {
      await this._evaluationService.stopClient(this.evaluation.name, computer.user).toPromise();
    } catch (error) {
      ErrorHandling.getErrorMessages(error, `Could not stop VM ${computer.resourceId}:`);
      computer.state = currentState;
    }
  }

  private async stopServer(computer: Computer, currentState: string) {
    try {
      await this._evaluationService.stopServer(this.evaluation.name).toPromise();
    } catch (error) {
      ErrorHandling.getErrorMessages(error, `Could not stop VM ${computer.resourceId}:`);
      computer.state = currentState;
    }
  }

  connect(computer: Computer) {
    const rdpString = this._rdpService.buildRdpFileAsString(computer, this.evaluation.vmUserName);
    const blob = new Blob([rdpString], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, this._rdpService.setRdpFileName(computer));
  }

  updateMessageBannerForDeployment(state: string) {
    this.stateMessages = [];
    switch (state) {
      case EvaluationStates.notStarted: {
        this.stateMessages.push({ severity: 'warn', summary: 'Not Started', detail: 'The Evaluation has not yet been initialized to start.' });
        break;
      }
      case EvaluationStates.deploying: {
        this.stateMessages.push({ severity: 'warn', summary: 'Deploying', detail: 'The Evaluation is currently deploying.' });
        break;
      }
      case EvaluationStates.started: {
        this.stateMessages.push({ severity: 'success', summary: 'Successfully Started', detail: 'The Evaluation has been deployed.' });
        break;
      }
      case EvaluationStates.ended: {
        this.stateMessages.push({ severity: 'success', summary: 'Successfully Ended', detail: 'The Evaluation has deallocated and successfully ended.' });
        break;
      }
      case EvaluationStates.deployFailed: {
        this.stateMessages.push({ severity: 'error', summary: 'Deployment Failed', detail: 'Something went wrong with the deployment.  Contact the Geophysical Insights team.' });
        break;
      }
      default: {
        break;
      }
    }
  }

  updateMessageBannerForClientState(state: string, computerName: string) {
    this.stateMessages = [];
    switch (state) {
      case ComputerStates.stopping: {
        this.stateMessages.push({ severity: 'warn', summary: 'Stopping', detail: 'The Paradise virtual machine (' + computerName + ') is stopping.' });
        break;
      }
      case ComputerStates.stopped: {
        this.stateMessages.push({ severity: 'success', summary: 'Stopped', detail: 'The Paradise virtual machine (' + computerName + ') has stopped.' });
        break;
      }
      case ComputerStates.running: {
        this.stateMessages.push({ severity: 'success', summary: 'Running', detail: 'The Paradise virtual machine (' + computerName + ') is running and ready.' });
        break;
      }
      case ComputerStates.starting: {
        this.stateMessages.push({ severity: 'warn', summary: 'Starting', detail: 'The Paradise virtual machine (' + computerName + ') is starting up.' });
        break;
      }
      // No status updates for Stopping, Stopped, and unknown at this point.
      default: {
        break;
      }
    }
  }

  back() {
    this._router.navigate(['/adminconnectlist/']);
  }

  reveal(computer: Computer) {
    if (this.revealList.includes(computer.resourceId)) {
      this.revealList = this.revealList.filter(r => r !== computer.resourceId);
    }
    else {
      this.revealList.push(computer.resourceId);
    }

  }

  ngOnDestroy() {
    this.stopRefreshing = true;
  }

  validateEmail() {
    let invalidEmailsCount = 0;
    if (this.evaluation.users != null && this.evaluation.users.length > 0) {
      this.evaluation.users.forEach(user => {
        if (!(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/).test(user)) {
          invalidEmailsCount++;
        }
      });
    }
    this.emailInvalid = invalidEmailsCount !== 0;
  }

  validateUserName() {
    this.userNameInvalid = this.evaluation.vmUserName &&
      (
        this.evaluation.vmUserName.length > 20 ||
        this.userNameConditions.some(el => this.evaluation.vmUserName.includes(el)) ||
        this.evaluation.vmUserName.endsWith('.')
      );
  }

  validatePassword() {
    this.passwordInvalid = !(this._validationService.validatePassword(this.evaluation.vmPassword));
  }
}



// WEBPACK FOOTER //
// ./src/app/components/admin-connect-detail/admin-connect-detail.component.ts